import { difficultyLevelData, DifficultyLevelType, GameName } from 'constants/difficulty';
import { pad } from 'game/helpers/format';
import { StoreLocalStorage } from './storage';
import { TABLET_WIDTH_BREAK_POINT } from 'constants/layout';
import { Layout } from 'game/LayoutManager';
import { Card } from 'components/Card';
import { SolitaireGame } from 'game/SolitaireGame';
import { MoveData } from 'game/Moves';
import { GameEvent } from 'game/events/gameEvent';
import { EventName } from 'game/events/eventsName';
import { freeCellGameState, spiderGameState, KlondikeMockStore } from './mock';
import { GAEvent, GAEventNames } from 'game/analytics/events';
import { valuesIn } from 'lodash';
import { theme } from 'game/theme';
import { fixedGameDifficulty } from './fixedGameDifficulty';

export const DEFAULT_HINT_COUNTER = 2;
export const DEFAULT_UNDO_COUNTER = 10; //unlimited
export const DEFAULT_WAND_COUNTER = 2;
export const ADS_RECEIVE_WAND_COUNTER = 1;

export type DealData = { id: string; deal: 'string' };
export const SCORE_NA = -999;
const defaultTheme = {
    front: '11_white_large',
    back: '71_classic_blue',
    background: '1_green',
};
export type GameWinScoreData = {
    minMoves: number;
    maxMoves: number;
    medianMoves: number;
    minTime: number;
    maxTime: number;
    medianTime: number;
};
export const emptyWinScore = {
    minMoves: SCORE_NA,
    maxMoves: SCORE_NA,
    medianMoves: SCORE_NA,
    minTime: SCORE_NA,
    maxTime: SCORE_NA,
    medianTime: SCORE_NA,
} as GameWinScoreData;
const defaultSettingsMobile = {
    soundOn: true,
    autocomplete: true,
    leftHanded: false,
    cardHighlighting: true,
    autoPlay: true,
};
const defaultSettingsDesktop = {
    soundOn: true,
    autocomplete: true,
    leftHanded: true,
    cardHighlighting: true,
    autoPlay: true,
};
const emptyLastGame = {
    columns: [] as { code: string; visible: boolean }[][],
    foundation: [] as { code: string; visible: boolean }[][],
    stockpile: [] as { code: string; visible: boolean }[],
    wastePile: [] as { code: string; visible: boolean }[],
    freeCells: [] as { code: string; visible: boolean }[][],
    gameId: '',
    name: '',
    score: 0,
    time: 0,
    movesCount: 0,
    moves: [] as MoveData[][], //for undo
    attempts: 0,
    magicWandUsed: 0,
    gameStarted: false,
};
class GameStore {
    difficulty = DifficultyLevelType.KLONDIKE_EASY_1CARD;
    dealCount = 1;
    suits = 1;
    movesCount = 0;
    score = 0;
    startTime = 0;
    stopTime = 0;
    gameActive = false;
    language = 'en';
    front = defaultTheme.front;
    back = defaultTheme.back;
    background = defaultTheme.background;
    gameId = '';
    isMobile = false;
    soundOn = true;
    autocomplete = false;
    leftHanded = false;
    cardHighlighting = false;
    autoPlay = false;

    gameStorage: StoreLocalStorage;
    undos = 0;
    hints = 0;
    deckTurns = 0;

    undoCounter = 0;
    hintCounter = 0;
    wandCounter = 0;

    attempts = 0;
    wins = 0;
    gameWins = {} as { [key in GameName]: number };
    name = GameName.KLONDIKE;
    previousGame = {
        difficulty: '', // not work for Random 3
        gameId: '',
        name: '', //klondike / spider
    };
    gameState = emptyLastGame;
    moves = [] as MoveData[][];
    facebookWinPopupNextShow = 10;
    notificationIcons = ['share', 'design', 'rules', 'settings'];
    gameOver = false;
    noAds = false;
    gameStarted = false;
    infoPopupShown = [] as string[];
    magicWandUsed = 0;
    feedbackCounterShown = 0;
    nextFeedbackDisplay = 20;
    notificationWinPopupNextShow = 5;

    private secondAssetPackLoaded = false;
    triggeredGameStarted = false;
    lowPerformanceDevice = true;
    popupLimits = {};
    deals = {
        [DifficultyLevelType.KLONDIKE_RANDOM_3CARDS]: [],
        [DifficultyLevelType.KLONDIKE_MEDIUM_3CARDS]: [],
        [DifficultyLevelType.KLONDIKE_HARD_3CARDS]: [],
        [DifficultyLevelType.KLONDIKE_EXPERT_3CARDS]: [],
        [DifficultyLevelType.KLONDIKE_MASTER_3CARDS]: [],

        [DifficultyLevelType.KLONDIKE_EASY_1CARD]: [],
        [DifficultyLevelType.KLONDIKE_MEDIUM_1CARD]: [],
        [DifficultyLevelType.KLONDIKE_HARD_1CARD]: [],
        [DifficultyLevelType.KLONDIKE_EXPERT_1CARD]: [],
        [DifficultyLevelType.KLONDIKE_MASTER_1CARD]: [],
        [DifficultyLevelType.KLONDIKE_RANDOM_1CARD]: [],

        [DifficultyLevelType.SPIDER_EASY_1SUIT]: [],
        [DifficultyLevelType.SPIDER_MEDIUM_1SUIT]: [],
        [DifficultyLevelType.SPIDER_EXPERT_1SUIT]: [],
        [DifficultyLevelType.SPIDER_MASTER_1SUIT]: [],
        [DifficultyLevelType.SPIDER_HARD_1SUIT]: [],
        [DifficultyLevelType.SPIDER_HARD_2SUIT]: [],
        [DifficultyLevelType.SPIDER_EXPERT_2SUIT]: [],
        [DifficultyLevelType.SPIDER_MASTER_2SUIT]: [],
        [DifficultyLevelType.SPIDER_EXPERT_4SUIT]: [],
        [DifficultyLevelType.SPIDER_MASTER_4SUIT]: [],

        [DifficultyLevelType.FREECELL_EASY]: [],
        [DifficultyLevelType.FREECELL_MEDIUM]: [],
        [DifficultyLevelType.FREECELL_HARD]: [],
        [DifficultyLevelType.FREECELL_EXPERT]: [],
        [DifficultyLevelType.FREECELL_MASTER]: [],
    } as { [key in DifficultyLevelType]: DealData[] };
    storiesViewed = false;
    storiesNextPopup = 0;
    gamesWinScore = null as null | GameWinScoreData;

    constructor() {
        this.mockDeal = this.mockDeal.bind(this);
        this.removeAds = this.removeAds.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.showFeedbackOnWin = this.showFeedbackOnWin.bind(this);

        this.gameStorage = new StoreLocalStorage();
        this.dealCount = this._getInitialDraw();
        this.suits = this._getInitialSuits();
        this.loadStorage();

        addEventListener('mockDeal', this.mockDeal, false);
        addEventListener('mockWins', this.mockWins.bind(this), false);
        addEventListener('noAds', this.removeAds, false);
        addEventListener('closeModal', this.closeModal, false);
        GameEvent.add(EventName.ON_VISIBILITY_CHANGE, this.onVisibleChange.bind(this));
        GameEvent.add(EventName.UI_ASSETS_LOADED, () => {
            this.secondAssetPackLoaded = true;
        });
        this.checkDevicePerformance();
    }
    checkDevicePerformance() {
        function runPerformanceTest() {
            const start = performance.now();
            for (let i = 0; i < 1e7; i++) {} // Tight loop
            const end = performance.now();
            const duration = end - start;
            // console.log(`Test completed in ${duration.toFixed(2)}ms`);
            return duration;
        }

        const perfScore = runPerformanceTest();
        if (perfScore > 200) {
            // console.log('Device seems low performance (slow CPU).');
        }

        function isLowEndDevice() {
            const cores = navigator.hardwareConcurrency || 2;
            const memory = navigator.deviceMemory || 2;
            const perf = runPerformanceTest();

            return cores <= 2 || memory <= 2 || perf > 200;
        }

        this.lowPerformanceDevice = isLowEndDevice();
    }
    get assetLoaded() {
        return this.secondAssetPackLoaded;
    }

    closeModal(e: CustomEvent) {
        GameEvent.emit(EventName.UNBLOCK_KEYBOARD);
        this.resumeGame();
        GameEvent.emit(EventName.CLOSE_MODAL, e.detail);
    }
    removeAds() {
        this.undoCounter = 9;
        this.hintCounter = 9;
        this.wandCounter = 9;
        this.gameStorage.setItem('undoCounter', 99);
        this.gameStorage.setItem('hintCounter', 99);
        this.gameStorage.setItem('wandCounter', 99);
        this.noAds = true;
    }
    hideFacebookWinPopup() {
        Store.facebookWinPopupNextShow = -1;
        this.gameStorage.setItem('facebookWinPopupNextShow', this.facebookWinPopupNextShow);
    }
    async loadStorage() {
        const isLoaded = await this.gameStorage.isSet();
        this.setGameName(this._getGameName() || GameName.KLONDIKE);
        this.previousGame = await this.gameStorage.getItem('previousGame', {});
        const difficulty = await this._getInitialDifficulty();

        this.difficulty = difficulty;

        this.gameState = await this.gameStorage.getItem(`gameState_${this.name}_${this.difficulty}`, this.gameState);
        if (this.gameState.gameId) {
            this.previousGame = {
                difficulty: this.difficulty,
                gameId: this.gameState.gameId,
                name: this.gameState.name,
            };
        }

        const displayMobile = window.innerWidth < TABLET_WIDTH_BREAK_POINT;
        if (displayMobile) {
            try {
                const userAgent = navigator.userAgent || navigator.vendor || window.opera;
                if (/android/i.test(userAgent)) this.isMobile = true;
                if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) this.isMobile = true;
            } catch (error) {}
        }
        const fallBack = this.isMobile ? defaultSettingsMobile : defaultSettingsDesktop;
        const storeValue = (status: boolean) => {
            return status ? 'ON' : 'OFF';
        };
        this.soundOn = (await this.gameStorage.getItem('soundOn', storeValue(fallBack.soundOn))) === 'ON';
        this.storiesViewed = localStorage.getItem('stories_tutorial') === 'shown';
        this.storiesNextPopup = parseInt(await this.gameStorage.getItem('storiesNextPopup', '0'));

        this.autocomplete =
            (await this.gameStorage.getItem('autocomplete', storeValue(fallBack.autocomplete))) === 'ON';
        this.leftHanded = (await this.gameStorage.getItem('leftHanded', storeValue(fallBack.leftHanded))) === 'ON';
        this.cardHighlighting =
            (await this.gameStorage.getItem('cardHighlighting', storeValue(fallBack.cardHighlighting))) === 'ON';
        this.autoPlay = (await this.gameStorage.getItem('autoPlay', storeValue(fallBack.autoPlay))) === 'ON';
        this.wins = parseInt(await this.gameStorage.getItem('wins', '0'));

        const gameWins = await this.gameStorage.getItem('gameWins', {});
        Object.keys(gameWins).forEach((key) => {
            this.gameWins[key as GameName] = parseInt(gameWins[key]);
        });

        this.facebookWinPopupNextShow = await this.gameStorage.getItem(
            'facebookWinPopupNextShow',
            this.facebookWinPopupNextShow
        );
        this.notificationIcons = await this.gameStorage.getItem('notificationIcons', this.notificationIcons);

        if (isLoaded) {
            this.front = await this.gameStorage.getItem('front', this.front);
            this.back = await this.gameStorage.getItem('back', this.back);
            this.background = await this.gameStorage.getItem('background', this.background);

            this.undoCounter = await this.gameStorage.getItem('undoCounter', DEFAULT_HINT_COUNTER);
            this.hintCounter = await this.gameStorage.getItem('hintCounter', DEFAULT_UNDO_COUNTER);
            this.wandCounter = await this.gameStorage.getItem('wandCounter', DEFAULT_WAND_COUNTER);
            this.infoPopupShown = await this.gameStorage.getItem('infoPopupShown', []);
            this.feedbackCounterShown = await this.gameStorage.getItem('feedbackCounterShown', 0);
            this.nextFeedbackDisplay = await this.gameStorage.getItem('nextFeedbackDisplay', 20);
            this.notificationWinPopupNextShow = await this.gameStorage.getItem('notificationWinPopupNextShow', 10);
        } else {
            this.gameStorage.setItem('version', this.gameStorage.currentVersion);
            this.gameStorage.setItem('soundOn', this.soundOn ? 'ON' : 'OFF');
            this.gameStorage.setItem('autocomplete', this.autocomplete ? 'ON' : 'OFF');
            this.gameStorage.setItem('leftHanded', this.leftHanded ? 'ON' : 'OFF');
            this.gameStorage.setItem('cardHighlighting', this.cardHighlighting ? 'ON' : 'OFF');
            this.gameStorage.setItem('autoPlay', this.autoPlay ? 'ON' : 'OFF');

            this.setHintCounter(DEFAULT_HINT_COUNTER);
            this.setUndoCounter(DEFAULT_UNDO_COUNTER);
            this.setWandCounter(DEFAULT_WAND_COUNTER);
        }
        this.setDifficulty(this.difficulty);

        this.checkGameAssetsStorage();
        this.setBack(this.back);
        this.setFront(this.front);
        this.setBackground(this.background);
    }
    setGameName(gameName: GameName) {
        Store.name = gameName;
    }
    checkGameAssetsStorage() {
        if (!theme.fronts.includes(this.front)) this.front = defaultTheme.front;
        if (!theme.backs.includes(this.back)) this.back = defaultTheme.back;
        if (!theme.backgrounds.includes(this.background)) this.background = defaultTheme.background;
    }
    private _getGameName() {
        const gameName = document.getElementById('game-container')?.getAttribute('data-name');
        if (gameName) {
            switch (gameName) {
                case GameName.KLONDIKE:
                case GameName.SPIDER:
                case GameName.FREECELL:
                    return gameName;
            }
        }
        return null;
    }

    private _getInitialDraw() {
        const draw = document.getElementById('game-container')?.getAttribute('data-draw');
        if (draw) return parseInt(draw);

        return 1;
    }

    private _getInitialSuits() {
        const draw = document.getElementById('game-container')?.getAttribute('data-suit');
        if (draw) return parseInt(draw);

        return 1;
    }

    private async _getInitialDifficulty() {
        let attrDifficulty = document.getElementById('game-container')?.getAttribute('data-difficulty'); //easy, medium, ect.
        let fixed = document.getElementById('game-container')?.getAttribute('data-difficulty-fixed') === 'true'; //easy, medium, ect.
        if (fixed && attrDifficulty) {
            return fixedGameDifficulty(attrDifficulty);
        }
        if (!fixed) {
            const difficultyStorage = await this.gameStorage.getItem('difficulty_' + this.getStoreName(), undefined);
            if (difficultyStorage && !fixed && !difficultyStorage.includes('RANDOM')) return difficultyStorage;
        }
        if (this.isKlondike) {
            let attrDraw = document.getElementById('game-container')?.getAttribute('data-draw'); //1 , 3
            if (attrDifficulty && attrDraw) {
                attrDifficulty = attrDifficulty.toUpperCase();
                let dif = valuesIn(DifficultyLevelType).find((value) => value === `${attrDifficulty}_${attrDraw}`);
                if (!dif || dif.includes('RANDOM')) {
                    dif =
                        attrDraw === '1'
                            ? DifficultyLevelType.KLONDIKE_EASY_1CARD
                            : DifficultyLevelType.KLONDIKE_MEDIUM_3CARDS;
                }
                return dif;
            }
            return DifficultyLevelType.KLONDIKE_MEDIUM_3CARDS;
        }
        if (this.isSpider) {
            switch (this._getInitialSuits()) {
                case 1:
                    return DifficultyLevelType.SPIDER_EASY_1SUIT;
                case 2:
                    return DifficultyLevelType.SPIDER_HARD_2SUIT;
                case 4:
                    return DifficultyLevelType.SPIDER_EXPERT_4SUIT;
            }
        }
        if (this.isFreecell) {
            return DifficultyLevelType.FREECELL_EASY;
        }
        return DifficultyLevelType.KLONDIKE_MEDIUM_3CARDS;
    }
    getStoreName() {
        let name = this.name as string;
        switch (this.name) {
            case GameName.KLONDIKE:
                name += this.dealCount;
                break;
            case GameName.SPIDER:
                name += this.suits;
                break;
        }
        return name;
    }

    setDifficulty(value: DifficultyLevelType) {
        this.gameStorage.setItem('difficulty_' + this.getStoreName(), value);
        this.difficulty = value;
    }
    setHintCounter(value: number) {
        if (this.noAds) return;
        this.hintCounter = value;
        this.gameStorage.setItem('hintCounter', value);
    }
    setUndoCounter(value: number) {
        if (this.noAds) return;

        this.undoCounter = value;
        this.gameStorage.setItem('undoCounter', value);
    }
    setWandCounter(value: number) {
        if (this.noAds) return;
        this.wandCounter = value;
        this.gameStorage.setItem('wandCounter', value);
    }

    getDifficultyText(difficulty?: DifficultyLevelType) {
        difficulty = difficulty || this.difficulty;
        return difficultyLevelData[difficulty].type;
    }
    getGameTypeText() {
        if (this.isKlondike) return this.getDealCountText();
        if (this.isSpider) return this.getSuitCountText();
        return 'not set';
    }

    getDealCountText(prevDiff?: DifficultyLevelType) {
        const diff = prevDiff || this.difficulty;

        if (diff.includes('3')) return '3 cards';
        else return '1 card';
    }
    getSuitCountText(prevSuit?: DifficultyLevelType) {
        const diff = prevSuit || this.difficulty;
        if (diff.includes('1')) return '1 suit';
        if (diff.includes('2')) return '2 suits';
        return '4 suits';
    }
    get time() {
        return Math.floor(
            (this.gameActive ? performance.now() - this.startTime : this.stopTime - this.startTime) / 1000
        );
    }
    get timeMin() {
        return Math.floor(
            (this.gameActive ? performance.now() - this.startTime : this.stopTime - this.startTime) / 1000 / 60
        );
    }
    getTimeFormat(timeInSec = 0) {
        const secInMinute = 60;
        const minutes = Math.floor(timeInSec / secInMinute).toString(); // Time in minutes
        const seconds = (timeInSec % secInMinute).toString(); // Time in seconds
        return `${pad(minutes)}:${pad(seconds)}`;
    }
    startGame() {
        if (!this.triggeredGameStarted) {
            dispatchEvent(new CustomEvent('gameStarted'));
            this.triggeredGameStarted = true;
        }
        if (this.gameStarted) return;
        this.startTime = performance.now();
        this.gameActive = true;
        this.gameStarted = true;

        const newGameEventData = {
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
            hand: Store.leftHanded ? 'Left' : 'Right',
            background: Store.background, //1_green
            card_back: Store.back,
            card_front: Store.front,
            sound: Store.soundOn ? 'On' : 'Off',
        } as { [key: string]: string };
        switch (Store.name) {
            case GameName.KLONDIKE:
                newGameEventData.autoPlay = Store.autoPlay ? 'On' : 'Off';

                break;
            case GameName.SPIDER:
                newGameEventData.highlighting = Store.cardHighlighting ? 'On' : 'Off';
                break;
            case GameName.FREECELL:
                newGameEventData.highlighting = Store.cardHighlighting ? 'On' : 'Off';
                newGameEventData.autoPlay = Store.autoPlay ? 'On' : 'Off';
                break;
        }
        GAEvent(GAEventNames.GAME_START, newGameEventData);
    }
    winGame() {
        Store.gameStarted = false;
        this.gameOver = true;
        this.wins++;
        this.gameStorage.setItem('wins', this.wins);
        this.gameWins[this.name] = this.gameWins[this.name] ? this.gameWins[this.name] + 1 : 1;

        this.gameStorage.setItem('gameWins', this.gameWins);
        this.stopTime = performance.now();
        this.gameActive = false;
    }
    pauseGame() {
        if (!this.gameActive) return;
        this.stopTime = performance.now();
        this.gameActive = false;
    }
    resumeGame() {
        if (this.gameActive) return;
        if (!this.gameStarted) return;
        this.startTime += performance.now() - this.stopTime;
        this.stopTime = 0;
        this.gameActive = true;
    }
    reset() {
        this.gameOver = false;
        this.movesCount = 0;
        this.startTime = 0;
        this.stopTime = 0;
        this.gameActive = false;
        this.gameStarted = false;
        this.undos = 0;
        this.hints = 0;
        this.deckTurns = 0;
        this.score = 0;
        if (this.isSpider) {
            this.score = 500;
        }
    }

    toggleSound() {
        this.soundOn = !this.soundOn;
        this.gameStorage.setItem('soundOn', this.soundOn ? 'ON' : 'OFF');
        return this.soundOn;
    }
    toggleAutocomplete() {
        this.autocomplete = !this.autocomplete;
        this.gameStorage.setItem('autocomplete', this.autocomplete ? 'ON' : 'OFF');
        return this.autocomplete;
    }
    toggleLeftHanded() {
        this.leftHanded = !this.leftHanded;
        this.gameStorage.setItem('leftHanded', this.leftHanded ? 'ON' : 'OFF');
        return this.leftHanded;
    }
    toggleCardHighlighting() {
        this.cardHighlighting = !this.cardHighlighting;
        this.gameStorage.setItem('cardHighlighting', this.cardHighlighting ? 'ON' : 'OFF');
    }
    toggleAutoPlay() {
        this.autoPlay = !this.autoPlay;
        this.gameStorage.setItem('autoPlay', this.autoPlay ? 'ON' : 'OFF');
    }
    setFront(frontFile: string) {
        this.front = frontFile;
        this.gameStorage.setItem('front', frontFile);
    }
    setBack(back: string) {
        this.back = back;
        this.gameStorage.setItem('back', back);
    }
    setBackground(background: string) {
        this.background = background;
        this.gameStorage.setItem('background', background);
    }

    getPreviousGame() {
        return this.previousGame;
    }
    getPreviousGameId(difficulty: DifficultyLevelType) {
        let fixed = document.getElementById('game-container')?.getAttribute('data-difficulty-fixed') === 'true';
        if (fixed) return null;

        if (!this.previousGame) return null;
        if (!this.gameState.gameId) return null;

        if (this.name === this.previousGame.name && this.previousGame.difficulty === difficulty)
            return this.previousGame.gameId;
        return null;
    }
    setPreviousGame() {
        this.previousGame = {
            difficulty: this.difficulty,
            gameId: this.gameId,
            name: this.name,
        };
        this.gameStorage.setItem('previousGame', this.previousGame);
    }
    getDealCount() {
        switch (this.name) {
            case GameName.KLONDIKE:
                return this.dealCount;
            case GameName.SPIDER:
                return Layout.columns;
            case GameName.FREECELL:
                return Layout.columns;
        }
    }
    get isSpider() {
        return this.name === GameName.SPIDER;
    }
    get isFreecell() {
        return this.name === GameName.FREECELL;
    }
    get isKlondike() {
        return this.name === GameName.KLONDIKE;
    }
    setGameState(game: SolitaireGame) {
        function mapTableCard(cards: Card[]) {
            return cards.map((card) => ({ code: card.code, visible: card.visible }));
        }

        const state = {
            gameId: this.gameId,
            columns: [...game.columns].map((column) => mapTableCard(column.cards)),
            foundation: [...game.foundations].map((column) => mapTableCard(column.cards)),
            name: this.name,
            movesCount: this.movesCount,
            moves: [], //this.moves, TODO change mapping place cards
            attempts: this.attempts,
            score: this.score,
            time: this.startTime - performance.now(),
            magicWandUsed: this.magicWandUsed,
            gameStarted: this.gameStarted,
        } as any;
        switch (this.name) {
            case GameName.KLONDIKE:
                if (game.wastePile) state.wastePile = mapTableCard(game.wastePile.cards);
                if (game.stockPile) state.stockpile = mapTableCard(game.stockPile.cards);
                this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, state);
                break;
            case GameName.SPIDER:
                if (game.stockPile) state.stockpile = mapTableCard(game.stockPile.cards);
                this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, state);
                break;
            case GameName.FREECELL:
                state.freeCells = [...game.freeCells].map((column) => mapTableCard(column.cards));
                this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, state);
                break;
        }
    }
    resetTable() {
        this.gameState = emptyLastGame;
        this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, emptyLastGame);
    }

    reloadLastGame() {
        this.attempts = this.gameState.attempts;
        this.magicWandUsed = this.gameState.magicWandUsed;
        this.gameStarted = this.gameState.gameStarted;
        if (this.gameState.movesCount === 0) return;
        this.startTime = this.gameState.time;
        this.gameActive = true;
        this.movesCount = this.gameState.movesCount;
        this.score = this.gameState.score;
    }
    hasHighlighting() {
        return this.name === GameName.SPIDER || this.name === GameName.FREECELL;
    }

    mockWins(event: CustomEvent) {
        if (event.detail) {
            this.wins = event.detail - 1;
            this.gameStorage.setItem('wins', this.wins);
            this.gameWins[this.name] = this.wins;
            this.gameStorage.setItem('gameWins', this.gameWins);
        }
    }
    mockDeal(event: CustomEvent) {
        if (event.detail.wins) {
            this.wins = 4;
            this.gameStorage.setItem('wins', this.wins);
            this.gameWins[this.name] = this.wins;
            this.gameStorage.setItem('gameWins', this.gameWins);

            this.facebookWinPopupNextShow = 10;
            this.gameStorage.setItem('facebookWinPopupNextShow', this.facebookWinPopupNextShow);
        }
        switch (this.name) {
            case GameName.FREECELL:
                this.autocompleteFreeCellMock(event.detail.check);
                break;
            case GameName.SPIDER:
                this.autocompleteSpiderMock();
                break;
            case GameName.KLONDIKE:
                this.autocompleteKlondikeMock();
                break;
        }
    }
    autocompleteFreeCellMock(check?: string) {
        this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, freeCellGameState);
        // window.location.reload();
    }
    autocompleteSpiderMock() {
        this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, spiderGameState);
        // window.location.reload();
    }
    autocompleteKlondikeMock() {
        this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, KlondikeMockStore);
        // window.location.reload();
    }
    onVisibleChange(visible: boolean) {
        if (this.gameActive) {
            if (visible) {
                this.startTime += performance.now() - this.stopTime;
            } else {
                this.stopTime = performance.now();
            }
        }
    }
    hasNotification(iconName: string) {
        return this.notificationIcons.includes(iconName);
    }
    removeNotification(iconName: string) {
        this.notificationIcons = this.notificationIcons.filter((icon) => icon !== iconName);
        this.gameStorage.setItem('notificationIcons', this.notificationIcons);
    }
    async loadAdsCounter() {
        this.undoCounter = await this.gameStorage.getItem('undoCounter', DEFAULT_HINT_COUNTER);
        this.hintCounter = await this.gameStorage.getItem('hintCounter', DEFAULT_UNDO_COUNTER);
        this.wandCounter = await this.gameStorage.getItem('wandCounter', DEFAULT_WAND_COUNTER);
    }
    shownPopup(popup: string) {
        this.pauseGame();
        this.infoPopupShown.push(popup);
        this.gameStorage.setItem('infoPopupShown', this.infoPopupShown);
    }
    hasPopup(popup: string) {
        return this.infoPopupShown.includes(popup);
    }
    blockFeedbackOnWin() {
        this.nextFeedbackDisplay = -1;
        this.gameStorage.setItem('nextFeedbackDisplay', this.nextFeedbackDisplay);
    }
    blockNotificationOnWin() {
        this.notificationWinPopupNextShow = -1;
        this.gameStorage.setItem('notificationWinPopupNextShow', this.notificationWinPopupNextShow);
    }
    showFeedbackOnWin() {
        if (this.nextFeedbackDisplay > -1 && this.wins >= this.nextFeedbackDisplay && this.feedbackCounterShown < 3) {
            this.nextFeedbackDisplay = Math.floor(this.wins / 20) * 20 + 20;
            this.feedbackCounterShown++;
            this.gameStorage.setItem('feedbackCounterShown', this.feedbackCounterShown);
            this.gameStorage.setItem('nextFeedbackDisplay', this.nextFeedbackDisplay);
            return true;
        }

        return false;
    }

    mockFeedbackOnWin(wins: number) {
        this.wins = wins - 1;
        this.nextFeedbackDisplay = Math.floor(this.wins / 20) * 20 + 20;

        this.gameStorage.setItem('nextFeedbackDisplay', this.nextFeedbackDisplay);
    }
    countPopupShown(popupId: string) {
        const popupCount = this.infoPopupShown.filter((item) => item === popupId);
        return popupCount.length;
    }
    setGameWinScore(score: null | GameWinScoreData) {
        if (score) {
            this.gamesWinScore = {
                maxMoves: score.maxMoves,
                maxTime: score.maxTime,
                medianMoves: Math.min(score.medianMoves + 30, score.maxMoves),
                medianTime: Math.min(score.medianTime + 60, score.maxTime),
                minMoves: score.minMoves,
                minTime: score.minTime,
            };
        } else {
            this.gamesWinScore = null;
        }
    }
}

export const Store = new GameStore();
